import { OnDestroy } from '@angular/core';
import { untilDestroyed } from '@ngneat/until-destroy';
import { isNil } from 'lodash';
import { RemoteDeployDataFacade } from 'mbs-ui-shared@facades/remote-deploy-data.facade';
import { StorageType, TypesForFFI, TypesForIntelligentRetention } from 'mbs-ui-shared@models';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { SAWithDestinations } from 'mbs-ui-shared@models/StorageAccountsWithDestinations';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { AdvancedStorageSettings, SupportMethodsForStepsFromPlan } from 'wizards/helpers/support-methods-for-steps-from-plan';
import { RestoreAsDiskEnum } from 'wizards/models/type-data-models';
import { WizardStepsManagerClass } from 'wizards/remote-management-wizards/strategy/wizard-steps-manager.class';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';

export interface WizardStorageManagerHook extends OnDestroy {
  isNBF?: boolean;
  isUnix?: boolean;
  isEdit?: boolean;
  isRDMode?: boolean;
  planType?: PlanTypes;
  FormModel: Record<string, Record<string, any>>;
}

export class WizardStorageManagerClass {
  // For SQL
  public showAdvanced = false;
  // For VMs
  public useS3AccelerationPossible = false;
  public selectedStorage: StorageConnection;
  // For common plans
  public invalidIntelligentRetentionStorage = false;
  public canBackupEmptyFolders = true;

  private storageAccounts: Array<SAWithDestinations> = [];
  private rdService: RemoteDeployDataFacade;
  private storage: StorageConnection;
  private advancedStorage: AdvancedStorageSettings;

  public get selectedStorageAmazonOrAzure(): boolean {
    return (
      this.selectedStorage?.StorageType === StorageType.AmazonS3 ||
      this.selectedStorage?.StorageType === StorageType.AmazonS3Eu ||
      this.selectedStorage?.StorageType === StorageType.Azure
    );
  }

  public get minPeriod(): number {
    return SupportMethodsForStepsFromPlan.getStorageMinPeriod(this.storage, this.advancedStorage);
  }

  constructor(
    private stepService: WizardStepsService,
    private stepsManager: WizardStepsManagerClass,
    private hooks: WizardStorageManagerHook
  ) {}

  setRDService(rdService: RemoteDeployDataFacade) {
    this.rdService = rdService;
  }

  getStorageAccounts(): Array<SAWithDestinations> {
    return this.hooks.isRDMode ? this.storageAccounts : null;
  }

  getStorageAccountByBucketId(id: string): SAWithDestinations {
    const index = this.storageAccounts.findIndex((account) => account.buckets.some((bucket) => bucket.id === id));

    return index !== -1 ? this.storageAccounts[index] : null;
  }

  changeStorageHandler(connection: StorageConnection): void {
    const isNbfHybrid = this.hooks.isNBF && this.hooks.FormModel.whereBackupStep?.IsHybridBackup;
    const cloudOrLocalConnection = isNbfHybrid ? this.getCloudOrLocalConnectionId() : connection;

    if (isNil(cloudOrLocalConnection)) {
      delete this.storage;

      return void this.stepsManager.setAdvancedOptionsFlagAfterChangeStorage();
    }

    if (this.isCompletelyNewStorage(cloudOrLocalConnection)) {
      this.stepsManager.registerUserInteractWithSteps();
    }

    this.storage = cloudOrLocalConnection;
    this.invalidIntelligentRetentionStorage = !TypesForIntelligentRetention.isSupported(cloudOrLocalConnection);

    if (
      this.hooks.isNBF &&
      this.hooks.FormModel &&
      this.hooks.FormModel.whereBackupStep &&
      this.hooks.FormModel.Storages.CloudStorages.length
    ) {
      this.stepService.updateProperty(
        'storage',
        'canShowImmutabilityByStorage',
        SupportMethodsForStepsFromPlan.needShowImmutability(
          this.hooks.FormModel.Storages.CloudStorages,
          this.hooks.FormModel.whereBackupStep.ConnectionID
        )
      );
    }

    if (this.hooks.FormModel.advancedOptionsFlags) {
      this.setAdvancedOptionsFlags();

      if (!this.hooks.isNBF)
        this.canBackupEmptyFolders = SupportMethodsForStepsFromPlan.updateCanBackupEmptyFolders(
          cloudOrLocalConnection.StorageType,
          this.hooks.planType
        );
    }

    this.updateInvalidFFIStorage(cloudOrLocalConnection);

    this.stepService.updateProperty('storage', 'isLocal', this.storage?.IsLocal);
    this.stepService.updateProperty('storage', 'minPeriod', this.minPeriod);
    this.stepService.updateProperty('storage', 'immutabilityPeriodDays', this.storage.ImmutabilityPeriodDays);
    this.stepService.updateProperty('storage', 'name', this.storage?.DisplayName);
    this.stepService.updateProperty('storage', 'type', this.storage?.StorageType);
    this.stepService.updateProperty('storage', 'isPrivateStorage', this.storage?.IsPrivateStorage);

    if (isNbfHybrid) this.updateStoragePropertyIfHybrid();
  }

  // TODO: Fix duplicate logic with changeStorageHandler
  changeVMStorageHandler(connection: StorageConnection): void {
    const isNbfHybrid = this.hooks.isNBF && this.hooks.FormModel.whereBackupStep?.IsHybridBackup;
    const cloudOrLocalConnection = isNbfHybrid ? this.stepsManager.getCloudOrLocalConnectionId() : connection;

    if (this.isCompletelyNewStorage(cloudOrLocalConnection)) {
      this.stepsManager.registerUserInteractWithSteps();
    }

    this.storage = cloudOrLocalConnection;
    this.invalidIntelligentRetentionStorage = !TypesForIntelligentRetention.isSupported(cloudOrLocalConnection);

    if (!cloudOrLocalConnection || isNil(cloudOrLocalConnection.StorageType)) {
      this.useS3AccelerationPossible = false;
      return;
    }

    if (this.hooks.FormModel.advancedOptionsFlags) {
      this.setAdvancedOptionsFlags();
    }

    if (this.hooks.FormModel.StoragesArray?.length) {
      this.selectedStorage = this.hooks.FormModel.StoragesArray.find(
        (storage) => storage.ID === this.hooks.FormModel?.whereBackupStep?.ConnectionID
      );
    }

    if (this.hooks.FormModel.Storages.CloudStorages?.length) {
      this.stepService.updateProperty(
        'storage',
        'canShowImmutabilityByStorage',
        SupportMethodsForStepsFromPlan.needShowImmutability(
          this.hooks.FormModel.Storages.CloudStorages,
          this.hooks.FormModel.whereBackupStep.ConnectionID
        )
      );
    }

    this.useS3AccelerationPossible =
      cloudOrLocalConnection.StorageType === StorageType.AmazonS3 ||
      cloudOrLocalConnection.StorageType === StorageType.AmazonS3Eu ||
      cloudOrLocalConnection.StorageType === StorageType.UbAmazonS3;
    this.updateInvalidFFIStorage(cloudOrLocalConnection);
    this.stepService.updateProperty('storage', 'isLocal', this.storage?.IsLocal);
    this.stepService.updateProperty('storage', 'minPeriod', this.minPeriod);
    this.stepService.updateProperty('storage', 'immutabilityPeriodDays', this.storage.ImmutabilityPeriodDays);
    this.stepService.updateProperty('storage', 'name', this.storage?.DisplayName);
    this.stepService.updateProperty('storage', 'type', this.storage?.StorageType);

    if (isNbfHybrid) this.updateStoragePropertyIfHybrid();
  }

  // TODO: Fix possible duplicate
  changeStorageSQLHandler(event: StorageConnection): void {
    if (isNil(event)) {
      delete this.storage;

      if (this.hooks.FormModel.advancedOptionsFlags) {
        this.hooks.FormModel.advancedOptionsFlags = {
          UseS3AccelerationPossible: false,
          AzureAccessTierType: false,
          StorageClassSettingPossible: false
        };

        this.showAdvanced = false;
      }

      return;
    }

    if (this.hooks.FormModel.advancedOptionsFlags) {
      this.storage = event;
      const storageType = this.storage?.StorageType;
      const isAmazonS3 = storageType === StorageType.AmazonS3 || storageType === StorageType.AmazonS3Eu;

      this.hooks.FormModel.advancedOptionsFlags = {
        UseS3AccelerationPossible: isAmazonS3,
        AzureAccessTierType: storageType === StorageType.Azure,
        StorageClassSettingPossible: isAmazonS3
      };

      this.showAdvanced = isAmazonS3 || storageType === StorageType.Azure;
    }

    this.stepService.updateProperty('storage', 'isLocal', this.storage?.IsLocal);
    this.stepService.updateProperty('storage', 'minPeriod', this.minPeriod);
    this.stepService.updateProperty('storage', 'immutabilityPeriodDays', this.storage.ImmutabilityPeriodDays);
    this.stepService.updateProperty('storage', 'name', this.storage?.DisplayName);
    this.stepService.updateProperty('storage', 'type', this.storage?.StorageType);
  }

  changeAdvancedStorageHandler(event: AdvancedStorageSettings) {
    this.advancedStorage = event;

    this.updateInvalidFFIStorage(this.storage);
    this.stepService.updateProperty('storage', 'minPeriod', this.minPeriod);
    this.stepService.updateProperty('storage', 'storageClass', this.advancedStorage.storageClass || null);
    this.stepService.updateProperty('storage', 'azureAccessTierType', this.advancedStorage.azureType || null);
  }

  updateStorages(): void {
    this.rdService.SAWithDestinations$.pipe(untilDestroyed(this.hooks)).subscribe((accounts: SAWithDestinations[]) => {
      this.storageAccounts = accounts;
      const storagesObject = SupportMethodsForStepsFromPlan.getStoragesFromStorageAccounts(accounts);
      if (!this.hooks.isRDMode && this.hooks.isNBF)
        SupportMethodsForStepsFromPlan.getFilteredStoragesByTypes(storagesObject, [StorageType.OpenStack]);
      this.hooks.FormModel.Storages = {
        ...storagesObject,
        LocalStorages:
          this.hooks.isRDMode && this.hooks.isUnix
            ? storagesObject.LocalStorages.filter((s) => s.StorageType !== StorageType.LocalFS)
            : storagesObject.LocalStorages
      };
    });
  }

  updateBucket(plan): void {
    let bucketName = '';

    for (let i = 0; i < this.storageAccounts.length; i++) {
      if (!bucketName) {
        for (let j = 0; j < this.storageAccounts[i].buckets.length; j++) {
          const bucket = this.storageAccounts[i].buckets[j];

          if (bucket.id === this.hooks.FormModel.whereBackupStep.ConnectionID) {
            bucketName = bucket.bucketName;

            break;
          }
        }
      } else break;
    }

    plan.Bucket = bucketName || plan.Bucket;
  }

  setRestoreAs(event: RestoreAsDiskEnum): void {
    if (!this.storageAccounts.length) return;

    if (!this.hooks.FormModel.azureData.accounts.length && this.isAzureEvent(event)) {
      this.setAzureAccounts();
    } else if (!this.hooks.FormModel.EC2Data.accounts.length && this.isEC2Event(event)) {
      this.setEC2Accounts();
    }
  }

  setAdvancedOptionsFlags(): void {
    this.stepsManager.setAdvancedOptionsFlags(this.storage);
  }

  private isAzureEvent(event: RestoreAsDiskEnum): boolean {
    return event === RestoreAsDiskEnum.ADD || event === RestoreAsDiskEnum.AVM;
  }

  private isEC2Event(event: RestoreAsDiskEnum): boolean {
    return event === RestoreAsDiskEnum.EC2 || event === RestoreAsDiskEnum.EBSVolume;
  }

  private setAzureAccounts(): void {
    const accounts = this.storageAccounts
      .filter((acc) => acc.storageType === StorageType.AzureVM)
      .map((acc) => ({ value: acc.id, label: acc.name }));

    this.hooks.FormModel.azureData = { ...this.hooks.FormModel.azureData, accounts };

    if (!this.hooks.FormModel.azureStep.account) {
      this.hooks.FormModel.azureStep.account = accounts[0].value;
    }
  }

  private setEC2Accounts(): void {
    const accounts = this.storageAccounts
      .filter((acc) => acc.storageType === StorageType.AmazonS3 || acc.storageType === StorageType.AmazonS3Eu)
      .map((acc) => ({ value: acc.id, label: acc.name }));

    this.hooks.FormModel.EC2Data = { ...this.hooks.FormModel.EC2Data, accounts };
    this.hooks.FormModel.temporaryData = { ...this.hooks.FormModel.temporaryData, accounts };

    if (!this.hooks.FormModel.EC2.account) this.hooks.FormModel.EC2.account = accounts[0].value;
    if (!this.hooks.FormModel.temporaryInstanceStep.account) this.hooks.FormModel.temporaryInstanceStep.account = accounts[0].value;
  }

  private getCloudOrLocalConnectionId(): StorageConnection {
    return (
      this.hooks.FormModel.Storages.LocalStorages.find((s) => s.ID === this.hooks.FormModel.whereBackupStep.ConnectionID) ||
      this.hooks.FormModel.Storages.CloudStorages.find((s) => s.ID === this.hooks.FormModel.whereBackupStep.ConnectionID)
    );
  }

  private isCompletelyNewStorage(connection: StorageConnection): boolean {
    return (!this.hooks.isEdit && !this.storage) || (this.storage?.ID && this.storage?.ID !== connection?.ID);
  }

  private updateInvalidFFIStorage(storage: StorageConnection): void {
    if (!storage) {
      return void this.stepService.updateProperty('storage', 'invalidFFIStorage', null);
    }

    const isStorageSupported = TypesForFFI.isSupported(storage, this.stepsManager.backupVersionUpdated);

    if (this.hooks.isNBF && (!isStorageSupported || this.advancedStorage?.isStorageBadForFFI)) {
      return void this.stepService.updateProperty('storage', 'invalidFFIStorage', storage.DisplayName);
    }

    this.stepService.updateProperty('storage', 'invalidFFIStorage', null);
  }

  private updateStoragePropertyIfHybrid(): void {
    const local = this.hooks.FormModel.Storages.LocalStorages.find((s) => s.ID === this.hooks.FormModel.whereBackupStep.HybridID);

    if (!local) return;

    this.stepService.updateProperty('storage', 'hybridIsLocal', local.IsLocal);
    this.stepService.updateProperty('storage', 'hybridName', local.DisplayName);
    this.stepService.updateProperty('storage', 'hybridType', StorageType.LocalFS);
  }
}
